// SPDX-License-Identifier: Apache-2.0

// docx → markdown via `mammoth`.
//
// Old Tether used `mammoth.extractRawText` (plain text). That collapses
// headings, bold/italic, and tables into a flat paragraph stream, which loses
// structure that the orbit + 3D viewer want for navigation. v2 upgrades to
// `mammoth.convertToHtml` and then runs a deliberate small HTML→markdown step
// so we keep:
//
//   - heading levels (H1–H6)
//   - bold / italic / code spans
//   - bulleted + numbered lists
//   - tables (rendered as GFM pipe-tables)
//
// We avoid pulling in turndown / unified just for this — the HTML mammoth
// emits is already a tight, predictable subset (no CSS, no scripts, only the
// element set Word can produce). A 200-line hand walker is faster and cheaper
// than dragging in a full HTML→MD library.
//
// Mammoth runs entirely in the browser; the import is dynamic so the main
// bundle doesn't pay for it on cold load.

import {
  asParagraphs,
  baseTitle,
  corruptResult,
  warnIfLarge,
  wrapWithTitle,
  type IngestResult,
} from './common';

interface MammothModule {
  convertToHtml: (input: { arrayBuffer: ArrayBuffer }) => Promise<{
    value: string;
    messages: Array<{ type: string; message: string }>;
  }>;
}

export async function parseDocx(file: File): Promise<IngestResult> {
  warnIfLarge(file, 'docx');
  const title = baseTitle(file.name);
  try {
    const mod = await import('mammoth');
    // Mammoth's CJS-via-ESM shape: the public surface lives on `default`,
    // but in some bundlers it's also flattened on the namespace.
    const mammoth = ((mod as unknown as { default?: MammothModule }).default ??
      (mod as unknown as MammothModule));

    const arrayBuffer = await file.arrayBuffer();
    const { value: html, messages } = await mammoth.convertToHtml({ arrayBuffer });
    if (messages.some((m) => m.type === 'error')) {
      const errs = messages.filter((m) => m.type === 'error').map((m) => m.message);
      console.warn('[ingest:docx] mammoth conversion warnings/errors:', errs);
    }
    const md = htmlToMarkdown(html);
    return {
      title,
      content: wrapWithTitle(title, asParagraphs(md)),
      front: {},
      kind: 'docx',
    };
  } catch (err) {
    return corruptResult(file.name, err instanceof Error ? err.message : String(err));
  }
}

// ─── HTML→MD walker ─────────────────────────────────────────────────────────
//
// Mammoth's output uses only this element set:
//   <h1>..<h6>  <p>  <strong>  <em>  <u>  <a>  <br>
//   <ul><li>    <ol><li>
//   <table><thead><tbody><tr><th><td>
//   <img>       <code>  <pre>
// We treat anything else as a transparent passthrough of children.

interface DomNode {
  tag: string | null; // null = text node
  text: string;
  attrs: Record<string, string>;
  children: DomNode[];
}

/** Tiny HTML parser sufficient for mammoth's output. NOT a general HTML parser
 *  — assumes well-formed tags, no scripts/styles, no comments outside what
 *  mammoth emits (it doesn't emit comments). Faster than DOMParser for this
 *  scope and works in non-DOM environments (bun:test). */
function parseHtml(html: string): DomNode {
  const root: DomNode = { tag: null, text: '', attrs: {}, children: [] };
  const stack: DomNode[] = [root];
  const VOID = new Set(['br', 'img', 'hr']);

  const re = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*?)(\/?)>|([^<]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const top = stack[stack.length - 1];
    if (!top) break;
    if (m[5] !== undefined) {
      // Text run.
      const text = decodeEntities(m[5]);
      if (text) top.children.push({ tag: null, text, attrs: {}, children: [] });
      continue;
    }
    const closing = m[1] === '/';
    const tag = (m[2] ?? '').toLowerCase();
    const attrsRaw = m[3] ?? '';
    const selfClosed = m[4] === '/' || VOID.has(tag);

    if (closing) {
      // Pop until we find a matching tag (forgive minor mismatches).
      for (let i = stack.length - 1; i >= 1; i--) {
        if (stack[i]?.tag === tag) {
          stack.length = i;
          break;
        }
      }
      continue;
    }

    const node: DomNode = {
      tag,
      text: '',
      attrs: parseAttrs(attrsRaw),
      children: [],
    };
    top.children.push(node);
    if (!selfClosed) stack.push(node);
  }
  return root;
}

function parseAttrs(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([a-zA-Z_:][\w:.-]*)\s*=\s*"([^"]*)"|([a-zA-Z_:][\w:.-]*)\s*=\s*'([^']*)'/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const key = (m[1] ?? m[3] ?? '').toLowerCase();
    const val = m[2] ?? m[4] ?? '';
    if (key) out[key] = decodeEntities(val);
  }
  return out;
}

function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n: string) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&'); // last so we don't double-decode
}

function htmlToMarkdown(html: string): string {
  const root = parseHtml(html);
  return renderBlocks(root.children).trim();
}

function renderBlocks(nodes: DomNode[]): string {
  const out: string[] = [];
  for (const n of nodes) {
    out.push(renderBlock(n));
  }
  return out.filter((s) => s.length > 0).join('\n\n');
}

function renderBlock(node: DomNode): string {
  const tag = node.tag;
  if (tag === null) {
    const t = node.text.trim();
    return t;
  }
  if (/^h[1-6]$/.test(tag)) {
    const level = Number(tag.slice(1));
    return `${'#'.repeat(level)} ${renderInline(node.children).trim()}`;
  }
  if (tag === 'p') {
    const inner = renderInline(node.children).trim();
    return inner;
  }
  if (tag === 'ul') {
    return node.children
      .filter((c) => c.tag === 'li')
      .map((li) => `- ${renderInline(li.children).trim()}`)
      .join('\n');
  }
  if (tag === 'ol') {
    return node.children
      .filter((c) => c.tag === 'li')
      .map((li, i) => `${i + 1}. ${renderInline(li.children).trim()}`)
      .join('\n');
  }
  if (tag === 'table') return renderTable(node);
  if (tag === 'pre') {
    const code = renderInline(node.children).trim();
    return `\`\`\`\n${code}\n\`\`\``;
  }
  if (tag === 'blockquote') {
    const inner = renderBlocks(node.children);
    return inner
      .split('\n')
      .map((l) => `> ${l}`)
      .join('\n');
  }
  if (tag === 'br' || tag === 'hr') {
    return tag === 'hr' ? '---' : '';
  }
  // Unknown wrapper — render children as blocks.
  return renderBlocks(node.children);
}

function renderInline(nodes: DomNode[]): string {
  let out = '';
  for (const n of nodes) {
    if (n.tag === null) {
      out += n.text;
      continue;
    }
    const inner = renderInline(n.children);
    switch (n.tag) {
      case 'strong':
      case 'b':
        out += `**${inner}**`;
        break;
      case 'em':
      case 'i':
        out += `*${inner}*`;
        break;
      case 'code':
        out += `\`${inner}\``;
        break;
      case 'a': {
        const href = n.attrs.href ?? '';
        out += href ? `[${inner}](${href})` : inner;
        break;
      }
      case 'br':
        out += '  \n';
        break;
      case 'img': {
        const src = n.attrs.src ?? '';
        const alt = n.attrs.alt ?? '';
        out += src ? `![${alt}](${src})` : '';
        break;
      }
      case 'u':
        // Markdown has no underline — fold to bold to keep emphasis.
        out += `**${inner}**`;
        break;
      default:
        // Inline-level wrapper we don't know — render its children inline.
        out += inner;
    }
  }
  return out;
}

function renderTable(node: DomNode): string {
  // Walk thead+tbody+rows. mammoth sometimes wraps rows in <tbody>, sometimes
  // emits bare <tr> directly under <table>.
  const rows: DomNode[][] = [];
  function walk(n: DomNode): void {
    if (n.tag === 'tr') {
      const cells = n.children.filter((c) => c.tag === 'th' || c.tag === 'td');
      if (cells.length > 0) rows.push(cells);
      return;
    }
    for (const c of n.children) walk(c);
  }
  walk(node);
  if (rows.length === 0) return '';

  const widths = (rows[0] ?? []).length;
  const renderRow = (cells: DomNode[]): string =>
    `| ${cells.map((c) => renderInline(c.children).replace(/\|/g, '\\|').trim()).join(' | ')} |`;

  const header = renderRow(rows[0] ?? []);
  const sep = `|${' --- |'.repeat(widths)}`;
  const body = rows.slice(1).map(renderRow);
  return [header, sep, ...body].join('\n');
}
